// WP16 browse-first: the data layer for Browse, the category / row pages, listing detail and the plan tray.
// `POST /browse` works without a session (the public key only), so nothing here creates one until the first Add.
import { useSyncExternalStore } from 'react'
import { fn } from '../config'
import { supabase, ApiError, callFn, track } from './api'
import { takeEarlyBrowse } from './earlyBrowse'
import { markBrowseStale, readBrowse, writeBrowse, type Hit } from './browseCache'
import { onOwnerChange, ownerNow, watchOwner } from './owner'
import { addDays, todayYmd, weekendRange } from './intake'
import { rememberPlan } from './intake'
import type { PlanItemData } from './plan'
import { flag } from './flags'
import { wordsNow } from './words'
import { laneFlag } from './laneFlag'

// ---------- types (server contract, build/handoff/WP16-browse-first-server.md part 1) ----------
/** explore (taste v2): a For you card picked from a kind of thing they have not touched yet ("Something new for you"). */
export type Card = { item: PlanItemData; km: number | null; when: { start: string; end: string | null } | null; added: boolean; explore?: boolean }
export type Tile = { cat: string; count: number }
export type BrowseRow = { key: string; items: Card[] }
export type BrowseRes = {
  city: { slug: string; label: string; tz: string }
  center: { lat: number; lng: number; label: string } | null
  personalized: boolean
  total: number
  tiles: Tile[]
  /** R-60: how many activities each mood collection holds under the current filters (absent from an older server). */
  collections?: { key: string; count: number }[]
  rows: BrowseRow[]
  items: Card[]
  next_offset: number | null
  resolve?: { status: string; label?: string; slug?: string }
  /** V2: why a view came back empty (building | radius | filters | none). */
  empty_reason?: 'building' | 'radius' | 'filters' | 'none' | string
  /** V2: the plan this view is re-ranked for (`plan` in the request). */
  plan?: { id: string; title?: string | null; label?: string | null; city?: string | null }
  /** fr4: their home area is still being built (more activities arrive over the next minutes). */
  area_building?: boolean
  area?: { state: string; found_so_far?: number } | null
}

/** The sixteen categories in the spec's tile order (flow3-final §1). */
export const CATS = ['art', 'film', 'music', 'food', 'drink', 'comedy', 'outdoors', 'class', 'market', 'talks', 'fitness', 'community', 'theater', 'nightlife', 'day-trip', 'other'] as const

// ---------- filters (live in the URL query so a link reproduces the view) ----------
/** `week` (B-136): today through the next 6 days. */
export type When = 'any' | 'today' | 'tomorrow' | 'week' | 'weekend' | 'dates'
export type Filters = {
  where: string // what they typed or picked ('' = the city default)
  lat: number | null
  lng: number | null
  miles: number | null // null = any distance (or, from a saved home, the home default until anyDist)
  /** fr3 fix (QA prod r1): from a saved home, "Any distance" was picked on purpose (URL mi=any); otherwise the home
   *  default distance applies (HOME_MILES, which the server widens when the spot is thin). */
  anyDist: boolean
  when: When
  from: string | null
  to: string | null
  cats: string[]
  free: boolean
  hidden: boolean
  setting: 'indoor' | 'outdoor' | null
  plan: string | null // V2: re-rank for this plan ("Picked for this plan")
  /** 10/16 (R-94): include / exclude words, kept on the device (lib/words.ts), not in the URL. */
  inc: string[]
  exc: string[]
  /** 10/16 (R-08): well known / local favorites; under the radar stays `hidden` (its URL, row and tile already exist).
   *  None = a good mix. In the URL as fame=well,local. */
  fame: Fame[]
  /** 10/17 (R-103): parts of day and weekdays (0 = Sunday); none = any. In the URL as parts=morning,late and days=2,3. */
  parts: DayPart[]
  days: number[]
}
export type DayPart = 'morning' | 'afternoon' | 'evening' | 'late'
export const DAY_PARTS: DayPart[] = ['morning', 'afternoon', 'evening', 'late']
export type Fame = 'well' | 'local'
export const FAMES: Fame[] = ['well', 'local']
export const NO_FILTERS: Filters = { where: '', lat: null, lng: null, miles: null, anyDist: false, when: 'any', from: null, to: null, cats: [], free: false, hidden: false, setting: null, plan: null, inc: [], exc: [], fame: [], parts: [], days: [] }

export function readFilters(q: URLSearchParams): Filters {
  const num = (k: string) => {
    const v = q.get(k)
    const n = v === null || v === '' ? NaN : Number(v)
    return Number.isFinite(n) ? n : null
  }
  const when = (q.get('when') as When) || 'any'
  const miles = num('mi')
  return {
    where: q.get('where') ?? '',
    lat: num('lat'),
    lng: num('lng'),
    miles: miles !== null && miles >= 1 && miles <= 100 ? Math.round(miles) : null,
    anyDist: q.get('mi') === 'any',
    when: ['any', 'today', 'tomorrow', 'week', 'weekend', 'dates'].includes(when) ? when : 'any',
    from: q.get('from'),
    to: q.get('to'),
    cats: (q.get('cats') ?? '').split(',').filter((c) => (CATS as readonly string[]).includes(c)),
    free: q.get('free') === '1',
    hidden: q.get('hidden') === '1',
    setting: q.get('setting') === 'indoor' || q.get('setting') === 'outdoor' ? (q.get('setting') as 'indoor' | 'outdoor') : null,
    plan: /^[0-9a-f-]{36}$/i.test(q.get('plan') ?? '') ? q.get('plan') : null,
    ...wordsNow(),
    fame: FAMES.filter((x) => (q.get('fame') ?? '').split(',').includes(x)),
    parts: DAY_PARTS.filter((x) => (q.get('parts') ?? '').split(',').includes(x)),
    days: [0, 1, 2, 3, 4, 5, 6].filter((d) => (q.get('days') ?? '').split(',').includes(String(d))),
  }
}

export function writeFilters(f: Filters, base?: URLSearchParams): URLSearchParams {
  const q = new URLSearchParams(base)
  const set = (k: string, v: string | null | undefined) => (v ? q.set(k, v) : q.delete(k))
  set('where', f.where)
  set('lat', f.lat !== null ? f.lat.toFixed(4) : null)
  set('lng', f.lng !== null ? f.lng.toFixed(4) : null)
  set('mi', f.miles !== null ? String(f.miles) : f.anyDist ? 'any' : null)
  set('when', f.when !== 'any' ? f.when : null)
  set('from', f.when === 'dates' ? f.from : null)
  set('to', f.when === 'dates' && f.to && f.to !== f.from ? f.to : null)
  set('cats', f.cats.join(','))
  set('free', f.free ? '1' : null)
  set('hidden', f.hidden ? '1' : null)
  set('setting', f.setting)
  set('plan', f.plan)
  set('fame', (f.fame ?? []).join(','))
  set('parts', (f.parts ?? []).join(','))
  set('days', (f.days ?? []).join(','))
  return q
}

/** How many filters beyond where / distance / when are on ("2 more filters" in the summary). */
// all three fame picks = a good mix, the same as none (the server applies no filter): they count as nothing
const fameAll = (f: Filters) => flag('fame_filter') && f.hidden && FAMES.every((x) => f.fame.includes(x))
export const extraCount = (f: Filters) => f.cats.length + (f.free ? 1 : 0) + (f.hidden && !fameAll(f) ? 1 : 0) + (f.setting ? 1 : 0)
  + (flag('fame_filter') && !fameAll(f) ? f.fame.length : 0) + (flag('words_filter') ? (f.inc.length ? 1 : 0) + (f.exc.length ? 1 : 0) : 0)
  + (laneFlag('time_filter') ? (timeOn(f).parts ? 1 : 0) + (timeOn(f).days ? 1 : 0) : 0)
/** R-103: the time chips that limit anything (all four parts or all seven days = no limit, as on the server). */
export const timeOn = (f: Filters) => ({
  parts: (f.parts ?? []).length > 0 && (f.parts ?? []).length < DAY_PARTS.length ? f.parts : null,
  days: (f.days ?? []).length > 0 && (f.days ?? []).length < 7 ? f.days : null,
})

export function dateRange(f: Filters): { from: string; to: string } | null {
  const t = todayYmd()
  if (f.when === 'today') return { from: t, to: t }
  if (f.when === 'tomorrow') return { from: addDays(t, 1), to: addDays(t, 1) }
  if (f.when === 'week') return { from: t, to: addDays(t, 6) }
  if (f.when === 'weekend') return weekendRange()
  if (f.when === 'dates' && f.from) return { from: f.from, to: f.to && f.to >= f.from ? f.to : f.from }
  return null
}

/** "Any distance" sends no limit the server could mistake for its 10-mile default: a radius past any city's edge. */
const ANY_KM = 1000
export const KM_PER_MI = 1.609344
/** The distance a saved home starts at: the server's own default (16 km), widened by the server when the spot is thin. */
export const HOME_MILES = 10

/** `home`: the person has a saved home and no place in the filters, so the server centers on that home (it knows it from
 *  the session); only a distance they picked (or Any distance) is sent, otherwise the server's home default applies. */
export function requestBody(f: Filters, extra: { cat?: string; row?: string; collection?: string; offset?: number } = {}, home = false): Record<string, unknown> {
  const body: Record<string, unknown> = {}
  if (f.lat !== null && f.lng !== null) body.point = { lat: f.lat, lng: f.lng }
  else if (f.where.trim()) body.where = f.where.trim()
  if (body.point || body.where) body.radius_km = f.miles !== null ? Math.round(f.miles * KM_PER_MI * 10) / 10 : ANY_KM
  else if (home && (f.miles !== null || f.anyDist)) body.radius_km = f.miles !== null ? Math.round(f.miles * KM_PER_MI * 10) / 10 : ANY_KM
  const r = dateRange(f)
  if (r) Object.assign(body, r)
  const cats = extra.cat ? [extra.cat] : f.cats
  if (cats.length) body.cats = cats
  if (f.free) body.free = true
  // R-08: well known / local favorites / under the radar are one choice (any of the picked); without fame, hidden as before
  if (flag('fame_filter') && f.fame.length) body.fame = [...f.fame, ...(f.hidden ? ['radar'] : [])]
  else if (f.hidden) body.hidden = true
  // R-94: the words travel with the request, so rows and pages still fill
  if (flag('words_filter') && f.inc.length) body.include = f.inc
  if (flag('words_filter') && f.exc.length) body.exclude = f.exc
  if (f.setting) body.setting = f.setting
  // R-103: parts of day and weekdays, applied by the server to every row and page
  if (laneFlag('time_filter')) { const t = timeOn(f); if (t.parts) body.parts = t.parts; if (t.days) body.days = t.days }
  if (f.plan) body.plan = f.plan
  if (extra.row) body.row = extra.row
  if (extra.collection) body.collection = extra.collection
  if (extra.offset) body.offset = extra.offset
  return body
}

// ---------- the call (no session needed) ----------
// The tab's cache of answers: lib/browseCache.ts (whose answer it is, how long it stays fresh, taste signals make it stale).
watchOwner(supabase.auth)
/** This user's cached answer for this request (null = none, someone else's, or too old); `fresh` = no need to ask again. */
export function cachedHit(key: string): Hit<BrowseRes> | null {
  return readBrowse<BrowseRes>(key, ownerNow())
}
export function cached(key: string): BrowseRes | null {
  return cachedHit(key)?.res ?? null
}
/** Keep an answer for this request (`at`: the age of the answer a page is appended to). */
export function remember(key: string, res: BrowseRes, at?: number, uid: string | null = ownerNow()) {
  writeBrowse(key, uid, res, at)
}

/** POST /browse. Any failure (404 included: a stale or someone else's `?plan=`) is an error the screen shows; there is
 *  no fallback to the raw pool (review H1: it silently dropped ranking, rows and city for the rest of the session). */
export async function browse(body: Record<string, unknown>, cacheKey?: string): Promise<BrowseRes> {
  const who = ownerNow() // the answer is cached as the person who asked (a sign-out mid-request never relabels it)
  // F5: the front page's first call may already be in flight from index.html (lib/earlyBrowse.ts says when it counts)
  if (window.__mgEarlyBrowse) {
    const token = (await supabase.auth.getSession()).data.session?.access_token ?? null
    const got = await takeEarlyBrowse<BrowseRes>(JSON.stringify(body), token)
    if (got) {
      if (cacheKey && who === ownerNow()) remember(cacheKey, got, undefined, who)
      return got
    }
  }
  const { data, error } = await supabase.functions.invoke(fn('browse'), { body })
  if (error) {
    let status: number | null = null
    let message = error.message
    try {
      const ctx = (error as { context?: Response }).context
      if (ctx) {
        status = ctx.status ?? null
        message = (await ctx.json()).error ?? message
      }
    } catch { /* keep the default */ }
    throw new ApiError(message, status)
  }
  const res = data as BrowseRes
  if (cacheKey && who === ownerNow()) remember(cacheKey, res, undefined, who)
  return res
}

// ---------- the plan a browse session builds (no dates, no questions) ----------
export type AddState = { status: 'adding' | 'added' | 'failed'; planItemId?: string }
type State = {
  planId: string | null
  count: number | null // confirmed items in the plan (null = not known yet)
  saving: boolean
  failed: boolean
  added: Record<string, AddState>
  // otherDates: it went to a plan without dates (R10). first: the person's first save, so the Saved bar carries the consent line (B-120)
  lastAdded: { itemId: string; at: number; otherDates?: boolean; first?: boolean } | null
  emptied: boolean // the last item was just removed
  consentShown: boolean
}
const PLAN_KEY = 'mg_browse_plan'
const CONSENT_KEY = 'mg_consent_seen'
const read = (k: string) => {
  try {
    return localStorage.getItem(k)
  } catch {
    return null
  }
}
const write = (k: string, v: string | null) => {
  try {
    if (v === null) localStorage.removeItem(k)
    else localStorage.setItem(k, v)
  } catch {
    /* convenience only */
  }
}

let state: State = { planId: read(PLAN_KEY), count: null, saving: false, failed: false, added: {}, lastAdded: null, emptied: false, consentShown: read(CONSENT_KEY) === '1' }
const subs = new Set<() => void>()
function setState(p: Partial<State> | ((s: State) => Partial<State>)) {
  state = { ...state, ...(typeof p === 'function' ? p(state) : p) }
  subs.forEach((f) => f())
}
const subscribe = (f: () => void) => {
  subs.add(f)
  return () => subs.delete(f)
}
export const useBrowsePlan = () => useSyncExternalStore(subscribe, () => state)
export const browsePlanId = () => state.planId
/** This tab's record of adding an item (outside React: lib/saved.ts settleSaved). */
export const addedNow = (itemId: string) => state.added[itemId]

/** Cards the server already marks as added (a returning person) seed the local state. */
export function seedAdded(cards: Card[]) {
  const fresh = cards.filter((c) => c.added && !state.added[c.item.id])
  if (!fresh.length) return
  setState((s) => ({ added: { ...s.added, ...Object.fromEntries(fresh.map((c) => [c.item.id, { status: 'added' as const }])) } }))
}

/** Mark the consent line seen (it shows once, beside the first Add). */
export function consentSeen() {
  write(CONSENT_KEY, '1')
  setState({ consentShown: true })
}

/** The server's answer when an event can't go in a dated plan (plan/index.ts placeItem): "outside your dates" or
 *  "on a different day than this plan". Only these send the add on to a plan without dates. */
const offDates = (e: unknown) => e instanceof ApiError && e.status === 400 && /outside your dates|different day than this plan/i.test(e.message)

const inflight = new Map<string, Promise<void>>()
/** Add a thing: the first add starts the anonymous session and the plan; repeated taps never duplicate.
 *  `toPlan`: a plan page's own plan (an Add there goes to that plan, V2-API-CHANGES 2); otherwise the draft. */
export function addItem(item: { id: string; title: string; category: string }, toPlan?: string, opts: { force?: boolean } = {}): Promise<void> {
  const cur = state.added[item.id]
  // release 1016: "added" also marks a thing on a dated itinerary or in a group (the server's card flag, seedAdded), which is
  // not saved; Save (lib/saved.ts) forces the call so it lands in the draft (the server answers 409 if it already is there)
  if (cur && cur.status !== 'failed' && !(opts.force && cur.status === 'added')) return inflight.get(item.id) ?? Promise.resolve()
  // a second item while the very first add is still creating the plan waits for it, so only one plan is made
  const first = !state.planId ? [...inflight.values()][0] : undefined
  setState((s) => ({ added: { ...s.added, [item.id]: { status: 'adding' } }, saving: true, failed: false, emptied: false }))
  const run = (async () => {
    try {
      if (first) await first.catch(() => {})
      // V2 (Alvin 10/07): Add always drops into the person's one draft plan; the server picks it when no plan id is sent
      const add: Record<string, unknown> = { item: item.id }
      if (toPlan) add.plan = toPlan
      type AddRes = { plan_item: { id: string }; plan?: { id: string; title?: string | null; items?: number } }
      let res: AddRes
      let otherDates = false
      try {
        res = await callFn<AddRes>('plan', { add })
      } catch (e) {
        // R10: an event outside the dated plan's dates never fails: add it with no plan id, so the server uses (or starts)
        // a plan without dates, which then becomes the plan Browse adds to; the tray says so (plan.add_other_dates)
        if (!add.plan || !offDates(e)) throw e
        res = await callFn<AddRes>('plan', { add: { item: item.id } })
        otherDates = true
      }
      // an add to a plan page's own plan leaves the tray on the draft
      const planId = toPlan && !otherDates ? state.planId : res.plan?.id ?? state.planId
      if (planId && planId !== toPlan) {
        write(PLAN_KEY, planId)
        rememberPlan(planId)
      }
      setState((s) => ({
        planId,
        count: toPlan && !otherDates ? s.count : res.plan?.items ?? (s.count ?? 0) + 1,
        added: { ...s.added, [item.id]: { status: 'added', planItemId: res.plan_item?.id } },
        lastAdded: { itemId: item.id, at: Date.now(), ...(otherDates ? { otherDates } : {}), ...(s.consentShown ? {} : { first: true }) },
      }))
      markBrowseStale() // B-163: the next Browse view asks again, so this Save shows in the order
      track('browse_add', { category: item.category, ...(otherDates ? { other_dates: true } : {}) })
      window.dispatchEvent(new Event('mg:add')) // R-52: the sign-in offer after a person's first Add (lib/signin.ts)
    } catch (e) {
      // already in the plan (added on another device or before a reload): show it as added, not as a failure
      if (e instanceof ApiError && e.status === 409) {
        setState((s) => ({ added: { ...s.added, [item.id]: { status: 'added' } } }))
        return
      }
      setState((s) => ({ added: { ...s.added, [item.id]: { status: 'failed' } }, failed: s.count === null }))
      throw e
    } finally {
      inflight.delete(item.id)
      setState({ saving: inflight.size > 0 })
    }
  })()
  inflight.set(item.id, run)
  run.catch(() => {})
  return run
}

/** Take a thing back out (Undo on the tray, Remove from plan on a card). */
/** Takes a thing this tab knows it added out of its plan. False when this tab has no record of adding it (nothing sent). */
export async function removeItem(itemId: string): Promise<boolean> {
  const cur = state.added[itemId]
  if (!cur || cur.status !== 'added') return false
  setState((s) => {
    const added = { ...s.added }
    delete added[itemId]
    const count = Math.max(0, (s.count ?? 1) - 1)
    return { added, count, emptied: count === 0, lastAdded: s.lastAdded?.itemId === itemId ? null : s.lastAdded }
  })
  try {
    let planItemId = cur.planItemId
    if (!planItemId && state.planId) {
      // a card the server marked as added: find its plan item
      const got = await callFn<{ unscheduled?: { id: string; item?: { id: string } | null }[]; days?: { items: { id: string; item?: { id: string } | null }[] }[] }>('plan', { get: state.planId })
      const all = [...(got.unscheduled ?? []), ...(got.days ?? []).flatMap((d) => d.items)]
      planItemId = all.find((s) => s.item?.id === itemId)?.id
    }
    if (planItemId) await callFn('plan', { remove: planItemId })
    markBrowseStale()
    return Boolean(planItemId)
  } catch (e) {
    setState((s) => ({ added: { ...s.added, [itemId]: cur }, count: (s.count ?? 0) + 1, emptied: false }))
    throw e
  }
}

/** Plan v3 (lib/saved.ts): an unsave the Saved list already sent; drop this session's record of it. */
export function forgetAdded(itemId: string) {
  markBrowseStale() // B-163: an unsave changes the order too
  if (!state.added[itemId]) return
  setState((s) => {
    const added = { ...s.added }
    delete added[itemId]
    return { added, count: s.count === null ? null : Math.max(0, s.count - 1), lastAdded: s.lastAdded?.itemId === itemId ? null : s.lastAdded }
  })
}

/** Retry the tray's failed first save. */
export function retryFailed() {
  const failed = Object.entries(state.added).find(([, a]) => a.status === 'failed')
  if (failed) {
    const snap = lastItems.get(failed[0])
    if (snap) void addItem(snap).catch(() => {})
  }
}
const lastItems = new Map<string, { id: string; title: string; category: string }>()
export const noteItem = (i: { id: string; title: string; category: string }) => lastItems.set(i.id, { id: i.id, title: i.title, category: i.category })

/** The plan this browse session builds: count from the server (for a returning visit). */
export async function refreshCount() {
  if (!state.planId) return
  try {
    const { data } = await supabase.auth.getSession()
    if (!data.session) return
    type Got = { plan: { id: string; start_date: string | null; is_draft?: boolean }; unscheduled?: { item?: { id: string } | null }[]; days?: { items: { item?: { id: string } | null }[] }[] }
    let got = await callFn<Got>('plan', { get: state.planId })
    // the plan Browse was adding to got dates, so it is no longer the draft (review L1): follow the draft instead
    if (got.plan.start_date || got.plan.is_draft === false) {
      const d = await callFn<{ plan: { id: string } }>('plan', { draft: true })
      write(PLAN_KEY, d.plan.id)
      rememberPlan(d.plan.id)
      got = await callFn<Got>('plan', { get: d.plan.id })
      setState({ planId: d.plan.id, added: {} })
    }
    const ids = [...(got.unscheduled ?? []), ...(got.days ?? []).flatMap((d) => d.items)].map((s) => s.item?.id).filter(Boolean) as string[]
    setState((s) => ({ count: ids.length, added: { ...Object.fromEntries(ids.map((id) => [id, { status: 'added' as const }])), ...s.added } }))
  } catch (e) {
    if (e instanceof ApiError && (e.status === 404 || e.status === 403)) {
      write(PLAN_KEY, null)
      setState({ planId: null, count: null })
    }
  }
}

/** "New plan" on the Plan tab's list: an empty plan without dates, which becomes the plan Browse adds to. */
export async function startEmptyPlan(): Promise<string> {
  const res = await callFn<{ plan: { id: string } }>('plan', { create: { flexible: true } })
  write(PLAN_KEY, res.plan.id)
  rememberPlan(res.plan.id)
  setState({ planId: res.plan.id, count: 0, added: {}, lastAdded: null, emptied: false })
  return res.plan.id
}

export function forgetPlan() {
  write(PLAN_KEY, null)
  setState({ planId: null, count: null, added: {}, lastAdded: null, emptied: false })
}

// ---------- Set aside: a reversible personal hold, kept on this device ----------
const ASIDE_KEY = 'mg_set_aside'
export type Aside = { id: string; item: PlanItemData; at: number }
let aside: Aside[] = (() => {
  try {
    return JSON.parse(read(ASIDE_KEY) ?? '[]') as Aside[]
  } catch {
    return []
  }
})()
const asideSubs = new Set<() => void>()
const asideSub = (f: () => void) => {
  asideSubs.add(f)
  return () => asideSubs.delete(f)
}
export const useAside = () => useSyncExternalStore(asideSub, () => aside)
// B-161: another person in this tab (Delete my data or sign-out in another tab, a new visitor): nothing of the old one stays
onOwnerChange(() => {
  inflight.clear()
  lastItems.clear()
  setState({ planId: null, count: null, saving: false, failed: false, added: {}, lastAdded: null, emptied: false, consentShown: false })
  aside = []
  asideSubs.forEach((f) => f())
})
export function toggleAside(item: PlanItemData) {
  aside = aside.some((a) => a.id === item.id) ? aside.filter((a) => a.id !== item.id) : [{ id: item.id, item, at: Date.now() }, ...aside]
  write(ASIDE_KEY, JSON.stringify(aside))
  asideSubs.forEach((f) => f())
}
